import { Card } from "@/components/ui/card";
import { formatMileage } from "@/utils/listingUtils";
import {
  Hash,
  Car,
  Calendar,
  Settings,
  Gauge,
  Users,
  Key,
  MapPin,
  FileText,
  Zap,
  Gauge as EngineIcon,
  Leaf,
  AlertTriangle,
  Palette,
  Fuel,
  Hash as ChassisIcon,
  DoorOpen,
  Clock,
  Sofa,
} from "lucide-react";
import { VehicleDetails } from "@/types/vehicleDetails";
import { VehicleTest } from "@/types/vehicleTest";
import { ListingDetailsVehicleTest } from "@/types/listing";
import { Button } from "../ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/context/ThemeContext";
import { useState } from "react";
import EquipmentItem from "./EquipmentItem";
import { sortEquipmentWithPriority } from "@/constants/equipmentCategories";
import {
  fuelTypeLabels,
  transmissionTypeLabels,
  getColorLabel,
  getBodyTypeLabel,
} from "@/i18n/labels/vehicleLabels";
import { carDetailsLabels, getCountryDisplayName } from "@/i18n";
import { TransmissionType, VehicleFuelType } from "@/types/enums";
import { formatDate } from "@/utils/dateUtils";
import { useLanguage } from "@/context/LanguageContext";
import { Lock } from "lucide-react";

interface VehicleInformationProps {
  vehicleDetails: Partial<VehicleDetails>;
  vehicleTest?: VehicleTest | ListingDetailsVehicleTest;
  showAdditionalInfo?: boolean;
  equipment?: string[];
  /** When true, show all equipment items with no "Visa mer" button (e.g. in document/PDF view). */
  showAllEquipment?: boolean;
  isGuestMode?: boolean;
  equipmentTotalCount?: number;
  onGuestLoginRequired?: () => void;
}

export const VehicleInformation = ({
  vehicleDetails,
  vehicleTest,
  showAdditionalInfo = true,
  equipment,
  showAllEquipment = false,
  isGuestMode = false,
  equipmentTotalCount,
  onGuestLoginRequired,
}: VehicleInformationProps) => {
  const iconStyle = "w-4 h-4 md:w-6 md:h-6";
  const isMobile = useIsMobile();
  const [showAll, setShowAll] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { language } = useLanguage();
  const viLabels = carDetailsLabels(language).vehicleInfo;

  // Helper function to get vehicleDetails from either VehicleTest or ListingDetailsVehicleTest
  const getVehicleDetails = () => {
    if (!vehicleTest) return vehicleDetails;

    // Check if it's a VehicleTest (has vehicleDetails property)
    if ("vehicleDetails" in vehicleTest) {
      // Merge test vehicleDetails with passed vehicleDetails to ensure variant is included
      return {
        ...vehicleDetails,
        ...vehicleTest.vehicleDetails,
      };
    }

    // For ListingDetailsVehicleTest, we don't have vehicleDetails, so return the passed vehicleDetails
    return vehicleDetails;
  };

  const testVehicleDetails = getVehicleDetails();

  // Prioritized/popular equipment first, then the rest. Then slice for visible count.
  const safeEquipment = sortEquipmentWithPriority(equipment ?? []);
  // On desktop, show first 9 items (3 rows * 3 columns), then show more/less button
  // On mobile, show first 6 items, then show more/less button.
  // When showAllEquipment is true (e.g. DocumentView), show all items and "Visa mer" is hidden.
  const visibleItems =
    showAllEquipment || showAll
      ? safeEquipment
      : isMobile
        ? safeEquipment.slice(0, 6)
        : safeEquipment.slice(0, 9);

  // Format transmission and fuel type from UPPERCASE enum to display text
  const formatTransmission = (transmission?: string): string => {
    if (!transmission) return viLabels.notSpecified;
    const transmissionLabels = transmissionTypeLabels(language);
    // Convert to uppercase for enum lookup
    const upperKey = transmission.toUpperCase().replace(/-/g, "_");
    // Try direct lookup with enum value first
    if (upperKey in TransmissionType) {
      const enumKey = upperKey as TransmissionType;
      if (transmissionLabels[enumKey]) return transmissionLabels[enumKey];
    }
    // Try mapping common values (lowercase input to enum)
    const mapping: Record<string, string> = {
      manual: transmissionLabels[TransmissionType.MANUAL] || "Manuell",
      automatic: transmissionLabels[TransmissionType.AUTOMATIC] || "Automat",
      semi_automatic:
        transmissionLabels[TransmissionType.SEMI_AUTOMATIC] || "Halvautomat",
      cvt: transmissionLabels[TransmissionType.CVT] || "CVT",
      other: transmissionLabels[TransmissionType.OTHER] || "Annat",
      manuell: "Manuell",
      automat: "Automat",
    };
    const lowerKey = transmission.toLowerCase().replace(/_/g, "_");
    return mapping[lowerKey] || transmission;
  };

  const formatFuelType = (fuelType?: string): string => {
    if (!fuelType) return viLabels.notSpecified;
    const fuelLabels = fuelTypeLabels(language);
    // Convert to uppercase for enum lookup
    const upperKey = fuelType.toUpperCase().replace(/-/g, "_");
    // Try direct lookup with enum value first
    if (upperKey in VehicleFuelType) {
      const enumKey = upperKey as VehicleFuelType;
      if (fuelLabels[enumKey]) return fuelLabels[enumKey];
    }
    // Try mapping common values (lowercase input to enum)
    const mapping: Record<string, string> = {
      gasoline: fuelLabels[VehicleFuelType.GASOLINE] || "Bensin",
      diesel: fuelLabels[VehicleFuelType.DIESEL] || "Diesel",
      electric: fuelLabels[VehicleFuelType.ELECTRIC] || "El",
      hybrid: fuelLabels[VehicleFuelType.HYBRID] || "Hybrid",
      plug_in_hybrid:
        fuelLabels[VehicleFuelType.PLUG_IN_HYBRID] || "Plug-in hybrid",
      ethanol: fuelLabels[VehicleFuelType.ETHANOL] || "Etanol",
      gas: fuelLabels[VehicleFuelType.GAS] || "Gas",
      other: fuelLabels[VehicleFuelType.OTHER] || "Annat",
      bensin: "Bensin",
      el: "El",
      etanol: fuelLabels[VehicleFuelType.ETHANOL] || "Etanol",
    };
    const lowerKey = fuelType.toLowerCase().replace(/_/g, "_");
    return mapping[lowerKey] || fuelType;
  };

  const notSpec = viLabels.notSpecified;
  const regVinFields = [
    {
      label: viLabels.regNumber,
      value: vehicleDetails.regNumber || notSpec,
      icon: <Hash className={iconStyle} />,
    },
    {
      label: viLabels.vin,
      value: testVehicleDetails?.vin || vehicleDetails.vin || notSpec,
      icon: <Car className={iconStyle} />,
    },
  ];
  const basicInfo = [
    ...(isGuestMode ? [] : regVinFields),
    {
      label: viLabels.model,
      value: vehicleDetails.model || notSpec,
      icon: <Car className={iconStyle} />,
    },
    ...(testVehicleDetails?.variant || vehicleDetails.variant
      ? [
          {
            label: viLabels.variant,
            value:
              testVehicleDetails?.variant || vehicleDetails.variant || notSpec,
            icon: <Car className={iconStyle} />,
          },
        ]
      : []),
    {
      label: viLabels.yearModel,
      value: vehicleDetails.year || notSpec,
      icon: <Calendar className={iconStyle} />,
    },
    {
      label: viLabels.transmission,
      value: formatTransmission(vehicleDetails.transmission),
      icon: <Settings className={iconStyle} />,
    },
    {
      label: viLabels.mileage,
      value: `${formatMileage(vehicleDetails.mileage)} mil`,
      icon: <Gauge className={iconStyle} />,
    },
    {
      label: viLabels.fuel,
      value: formatFuelType(vehicleDetails.fuelType),
      icon: <Fuel className={iconStyle} />,
    },
    {
      label: viLabels.color,
      value: getColorLabel(vehicleDetails.color, language) || notSpec,
      icon: <Palette className={iconStyle} />,
    },
    {
      label: viLabels.seats,
      value:
        testVehicleDetails?.seats?.toString() ||
        vehicleDetails.seats?.toString() ||
        notSpec,
      icon: <Users className={iconStyle} />,
    },
    {
      label: viLabels.bodyType,
      value: getBodyTypeLabel(vehicleDetails.bodyType, language) || notSpec,
      icon: <ChassisIcon className={iconStyle} />,
    },
  ];

  const additionalInfo = [
    {
      label: viLabels.owners,
      value:
        testVehicleDetails?.owners?.toString() ||
        vehicleDetails.owners?.toString() ||
        notSpec,
      icon: <Users className={iconStyle} />,
    },
    {
      label: viLabels.keys,
      value:
        testVehicleDetails?.keys?.toString() ||
        vehicleDetails.keys?.toString() ||
        notSpec,
      icon: <Key className={iconStyle} />,
    },
    {
      label: viLabels.origin,
      value: (() => {
        const raw = testVehicleDetails?.origin || vehicleDetails.origin;
        return raw ? getCountryDisplayName(raw, language) : notSpec;
      })(),
      icon: <MapPin className={iconStyle} />,
    },
    {
      label: viLabels.annualTax,
      value: (() => {
        const v =
          testVehicleDetails?.annualVehicleTax ??
          vehicleDetails.annualVehicleTax;
        if (v == null) return notSpec;
        const n = Number(v);
        if (Number.isNaN(n) || n < 0) return notSpec;
        return `${n.toLocaleString("sv-SE")} kr`;
      })(),
      icon: <FileText className={iconStyle} />,
    },
    // Only show Motor field if not electric vehicle
    ...(testVehicleDetails?.fuelType !== "El" &&
    testVehicleDetails?.fuelType !== "ELECTRIC" &&
    vehicleDetails.fuelType !== "El" &&
    vehicleDetails.fuelType !== "ELECTRIC"
      ? [
          {
            label: viLabels.engine,
            value:
              testVehicleDetails?.engine || vehicleDetails.engine || notSpec,
            icon: <EngineIcon className={iconStyle} />,
          },
        ]
      : []),
    {
      label: viLabels.horsepower,
      value:
        testVehicleDetails?.horsepower?.toString() ||
        vehicleDetails.horsepower?.toString() ||
        notSpec,
      icon: <Zap className={iconStyle} />,
    },
    // Only show electric drive range for electric vehicles
    ...(testVehicleDetails?.fuelType === "El" ||
    testVehicleDetails?.fuelType === "ELECTRIC" ||
    testVehicleDetails?.fuelType === "Elbil" ||
    vehicleDetails.fuelType === "El" ||
    vehicleDetails.fuelType === "ELECTRIC" ||
    vehicleDetails.fuelType === "Elbil"
      ? [
          {
            label: viLabels.electricRange,
            value: testVehicleDetails?.electricDriveRange
              ? `${testVehicleDetails.electricDriveRange} km`
              : vehicleDetails.electricDriveRange
                ? `${vehicleDetails.electricDriveRange} km`
                : notSpec,
            icon: <Zap className={iconStyle} />,
          },
        ]
      : []),
    // Only show motorvolym if not electric vehicle
    ...(testVehicleDetails?.fuelType !== "El" &&
    testVehicleDetails?.fuelType !== "ELECTRIC" &&
    vehicleDetails.fuelType !== "El" &&
    vehicleDetails.fuelType !== "ELECTRIC"
      ? [
          {
            label: viLabels.engineVolume,
            value: (() => {
              const engineVol =
                testVehicleDetails?.engineVolume || vehicleDetails.engineVolume;
              if (!engineVol) return notSpec;
              const num = parseFloat(String(engineVol));
              if (isNaN(num)) return notSpec;
              return num % 1 === 0 ? num.toFixed(1) : String(num);
            })(),
            icon: <EngineIcon className={iconStyle} />,
          },
        ]
      : []),
    {
      label: viLabels.emissionClass,
      value:
        testVehicleDetails?.emissionClass ||
        vehicleDetails.emissionClass ||
        notSpec,
      icon: <Leaf className={iconStyle} />,
    },
    {
      label: viLabels.accidentVehicle,
      value: (() => {
        const crashResult = vehicleTest?.vehicleTestData?.crash?.problem;
        if (crashResult === "unknown") return viLabels.unknown;
        if (crashResult === "no") return viLabels.no;
        if (crashResult === "yes") return viLabels.yes;
        return testVehicleDetails?.accidentVehicle ||
          vehicleDetails.accidentVehicle
          ? viLabels.yes
          : viLabels.no;
      })(),
      icon: <AlertTriangle className={iconStyle} />,
    },
    {
      label: viLabels.doors,
      value:
        testVehicleDetails?.doors?.toString() ||
        vehicleDetails.doors?.toString() ||
        notSpec,
      icon: <DoorOpen className={iconStyle} />,
    },
    {
      label: viLabels.lastInspection,
      value: testVehicleDetails?.lastInspectionDate
        ? formatDate(testVehicleDetails.lastInspectionDate)
        : vehicleDetails.lastInspectionDate
          ? formatDate(vehicleDetails.lastInspectionDate)
          : notSpec,
      icon: <Clock className={iconStyle} />,
    },
    {
      label: viLabels.upholstery,
      value:
        testVehicleDetails?.upholstery || vehicleDetails.upholstery || notSpec,
      icon: <Sofa className={iconStyle} />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Basic Vehicle Information */}
      <div className="p-2">
        <h3 className="text-base md:text-xl font-semibold mb-4 ">
          {viLabels.title}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {basicInfo.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className={`${isDark ? "text-white" : "text-gray-900"}`}>
                {item.icon}
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span
                  className={`text-base md:text-sm ${
                    isDark ? "text-white/60" : "text-gray-500"
                  }`}
                >
                  {item.label}:
                </span>
                <span
                  className={`text-base md:text-sm font-semibold break-words overflow-wrap-anywhere ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  {item.value}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Information */}
      {showAdditionalInfo && vehicleTest && (
        <div className="p-2">
          <h3 className="text-base md:text-xl font-semibold mb-4 ">
            {viLabels.additionalInfo}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {additionalInfo.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className={`${isDark ? "text-white" : "text-gray-900"}`}>
                  {item.icon}
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <span
                    className={`text-base md:text-sm ${
                      isDark ? "text-white/60" : "text-gray-500"
                    }`}
                  >
                    {item.label}:
                  </span>
                  <span
                    className={`text-base md:text-sm font-semibold break-words overflow-wrap-anywhere ${
                      isDark ? "text-white" : "text-gray-900"
                    } ${isGuestMode ? "text-muted-foreground italic" : ""}`}
                  >
                    {isGuestMode
                      ? carDetailsLabels(language).guest.valueHidden
                      : item.value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Equipment Information */}
      {equipment && (
        <div className="px-2 pt-2 pb-0">
          <h3 className="text-base md:text-xl font-semibold mb-4">
            {viLabels.equipment}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
            {visibleItems.map((item, index) => (
              <EquipmentItem key={index} item={item} />
            ))}
          </div>
          {!showAllEquipment &&
            (equipmentTotalCount ?? safeEquipment.length) >
              (isMobile ? 6 : 9) && (
            <Button
              className={`w-full mt-4 ${
                isGuestMode
                  ? "bg-muted text-muted-foreground hover:bg-muted"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
              onClick={() => {
                if (isGuestMode) {
                  onGuestLoginRequired?.();
                  return;
                }
                setShowAll(!showAll);
              }}
            >
              {isGuestMode ? (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  {viLabels.showMore}
                </>
              ) : showAll ? (
                viLabels.showLess
              ) : (
                viLabels.showMore
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
